import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { Save, User, FileText, Shield, AlertTriangle, Compass } from 'lucide-react';
import RateSettingsSection from '../components/RateSettingsSection';
import ChangePasswordSection from '../components/ChangePasswordSection';
import './SettingsPage.css';

// Allowed retention values shown in the UI. Backend accepts any int 1..3650
// but we keep the choice set small and predictable.
const RETENTION_OPTIONS = [
  { value: null, key: 'never' },
  { value: 30, key: 'd30' },
  { value: 90, key: 'd90' },
  { value: 365, key: 'd365' },
];

function formatChars(n) {
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, '')}K`;
  return String(n);
}

function SettingsPage() {
  const { t } = useTranslation('settings');
  const { user, updateUser } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [customPrompt, setCustomPrompt] = useState(user?.custom_prompt || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSaveName = async () => {
    if (!user) return;
    setSaving(true);
    try {
      const updated = await api.updateUser(user.id, { name });
      updateUser(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      console.error('Failed to update name:', error);
      alert(t('errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleSavePrompt = async () => {
    if (!user) return;
    setSaving(true);
    try {
      const updated = await api.updateCustomPrompt(user.id, customPrompt);
      updateUser(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      console.error('Failed to update custom prompt:', error);
      alert(t('errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleRetentionChange = async (newValue) => {
    if (!user) return;
    // Confirm before turning auto-delete ON (going from Never to a concrete N
    // days, or shortening an existing window).
    const current = user.auto_delete_days ?? null;
    const enabling = newValue !== null && (current === null || newValue < current);
    if (enabling) {
      const days = newValue;
      const ok = window.confirm(t('privacy.confirmEnable', { days }));
      if (!ok) return;
    }
    setSaving(true);
    try {
      // Setting retention here means the user already knows the policy, so ack
      // the notice popup too - no point showing it after a deliberate choice.
      const updated = await api.updateUser(user.id, {
        auto_delete_days: newValue,
        retention_notice_ack: true,
      });
      updateUser(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      console.error('Failed to update retention:', error);
      alert(t('errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  // Clearing the flag is all it takes - the tour lives in the layout around
  // this page, so it reappears over Settings the moment the flag drops.
  const handleRestartTour = async () => {
    if (!user) return;
    setSaving(true);
    try {
      const updated = await api.updateUser(user.id, { tour_completed: false });
      updateUser(updated);
    } catch (error) {
      console.error('Failed to restart tour:', error);
      alert(t('errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const currentRetention = user?.auto_delete_days ?? null;

  const charUsage = user?.char_usage;
  const usagePercent = charUsage && charUsage.free_limit
    ? Math.min(100, (charUsage.free_used / charUsage.free_limit) * 100)
    : 0;

  return (
    <div className="settings-page">
      <header className="settings-header">
        <h1>{t('title')}</h1>
        <p>{t('subtitle')}</p>
      </header>

      <div className="settings-sections">
        {/* Profile Section */}
        <section className="settings-section">
          <div className="section-header">
            <User size={20} />
            <h2>{t('profile.title')}</h2>
          </div>
          <div className="section-content">
            <div className="form-group">
              <label htmlFor="email">{t('profile.email')}</label>
              <input
                id="email"
                type="email"
                className="input"
                value={user?.email || ''}
                disabled
              />
              <p className="form-hint">{t('profile.emailHint')}</p>
            </div>

            <div className="form-group">
              <label htmlFor="name">{t('profile.name')}</label>
              <input
                id="name"
                type="text"
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('profile.namePlaceholder')}
              />
            </div>

            <button
              className="btn btn-primary"
              onClick={handleSaveName}
              disabled={saving || name === user?.name}
            >
              {saving ? <div className="spinner" /> : <Save size={16} />}
              {saved ? t('common:status.saved') : t('profile.saveChanges')}
            </button>
          </div>
        </section>

        <ChangePasswordSection />

        {/* Custom Prompt Section */}
        <section className="settings-section">
          <div className="section-header">
            <FileText size={20} />
            <h2>{t('preferences.title')}</h2>
          </div>
          <div className="section-content">
            <div className="form-group">
              <label htmlFor="customPrompt">{t('preferences.customInstructions')}</label>
              <textarea
                id="customPrompt"
                className="input textarea"
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                placeholder={t('preferences.placeholder')}
                rows={8}
              />
              <p className="form-hint">
                {t('preferences.hint')}
              </p>
            </div>

            <button
              className="btn btn-primary"
              onClick={handleSavePrompt}
              disabled={saving || customPrompt === user?.custom_prompt}
            >
              {saving ? <div className="spinner" /> : <Save size={16} />}
              {saved ? t('common:status.saved') : t('preferences.saveInstructions')}
            </button>
          </div>
        </section>

        {/* Privacy / Auto-delete Section */}
        <section className="settings-section">
          <div className="section-header">
            <Shield size={20} />
            <h2>{t('privacy.title')}</h2>
          </div>
          <div className="section-content">
            <div className="form-group">
              <label>{t('privacy.autoDeleteLabel')}</label>
              <p className="form-hint">{t('privacy.autoDeleteHint')}</p>
              <div className="retention-options">
                {RETENTION_OPTIONS.map((opt) => (
                  <label key={opt.key} className={`retention-option ${currentRetention === opt.value ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name="auto_delete_days"
                      checked={currentRetention === opt.value}
                      onChange={() => handleRetentionChange(opt.value)}
                      disabled={saving}
                    />
                    <span>{t(`privacy.options.${opt.key}`)}</span>
                  </label>
                ))}
              </div>
              {currentRetention !== null && (
                <div className="retention-warning">
                  <AlertTriangle size={14} />
                  <span>{t('privacy.activeWarning', { days: currentRetention })}</span>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Workspace Section */}
        <section className="settings-section">
          <div className="section-header">
            <User size={20} />
            <h2>{t('workspace.title')}</h2>
          </div>
          <div className="section-content">
            <div className="info-card">
              <div className="info-row">
                <span className="info-label">{t('workspace.plan')}</span>
                <span className={`info-value badge ${user?.workspace?.plan === 'limited' ? 'badge-warning' : 'badge-success'}`}>
                  {user?.workspace?.plan === 'limited'
                    ? t('workspace.planLimited')
                    : t('workspace.planUnlimited')}
                </span>
              </div>
              {charUsage && !charUsage.postpaid && (
                <>
                  <div className="info-row usage-row">
                    <span className="info-label">{t('workspace.freeThisMonth')}</span>
                    <div className="usage-detail">
                      <div className="settings-usage-bar">
                        <div
                          className="settings-usage-fill"
                          style={{ width: `${usagePercent}%` }}
                        />
                      </div>
                      <span className="usage-text">
                        {formatChars(charUsage.free_used)} / {formatChars(charUsage.free_limit)}
                      </span>
                    </div>
                  </div>
                  {charUsage.purchased_balance > 0 && (
                    <div className="info-row">
                      <span className="info-label">{t('workspace.purchasedBalance')}</span>
                      <span className="info-value">{formatChars(charUsage.purchased_balance)}</span>
                    </div>
                  )}
                  {charUsage.free_topped_up_to > charUsage.free_remaining && (
                    <div className="info-row">
                      <span className="info-label">
                        {t('workspace.resets', { amount: formatChars(charUsage.free_topped_up_to) })}
                      </span>
                      <span className="info-value">
                        {t('workspace.resetsInDays', { days: charUsage.days_until_reset })}
                      </span>
                    </div>
                  )}
                </>
              )}
              <div className="info-row">
                <span className="info-label">{t('workspace.pagesLimit')}</span>
                <span className="info-value">
                  10 {t('workspace.pagesPerDoc')}
                </span>
              </div>
            </div>
          </div>
        </section>

        <RateSettingsSection />

        <section className="settings-section">
          <div className="section-header">
            <Compass size={20} />
            <h2>{t('tour.title')}</h2>
          </div>
          <div className="section-content">
            <p className="form-hint">{t('tour.hint')}</p>
            <button
              className="btn btn-secondary"
              onClick={handleRestartTour}
              disabled={saving}
            >
              <Compass size={16} />
              {t('tour.restart')}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}

export default SettingsPage;
